/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { AdultOrChild, BookingFilter } from '../types/app'
import { ArticleType } from 'types/enums/ArticleType'
import { Article } from 'types/entities/Article'
import {
    CURRENCY,
    CURRENCY_FRACTION_DIGITS,
    DEFAULT_LANGUAGE,
    GUEST_ADDRESS_MANDATORY,
    GUEST_PHONE_EMAIL_MANDATORY,
    IS_PROD,
    LOCALE,
    PAYMENT_TEST,
    REQUIRE_FULL_ADDRESS_IN_REGISTRATION,
    TIMER_COUNT,
    Translations,
} from 'helpers/config'
import { ArticlesState } from 'store/articles/ArticlesReducer'
import store from 'helpers/store'
export { driverName, driverNameInvited } from 'utils/driverUtils'
import { PackageItemItemType } from 'types/enums/PackageItemItemType'
import { PackageItemPersonType } from 'types/enums/PackageItemPersonType'
import { Package } from 'types/entities/Package'
import { PersonType } from 'types/enums/PersonType'
import { PackageItem } from 'types/entities/PackageItem'
import { PackagesCartItem } from 'types/entities/PackagesCartItem'
import { PersonsType } from 'types/enums/PersonsType'
import { PackagesCart } from 'types/entities/PackagesCart'
import { Profile } from 'types/entities/Profile'
import { PackageUpsell } from 'store/booking/BookingAction'
import { BookingNavigationStep, BookingReducerState } from 'store/booking/BookingReducer'
import { uniquePackageWithActivityItem } from 'utils/packageUtils'
import { toast } from 'utils/toastUtils'
import { PriceOffer } from 'types/entities/PriceOffer'
import { Driver } from 'types/entities/Driver'
import i18n from 'i18next'
import ajax from '../helpers/ajax'
import { ReservationDriver } from 'types/entities/ReservationDriver'
import { UserType } from 'types/enums/UserType'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
export {getNewDateObject} from 'utils/calendarNavigationDate'
import Dates from 'utils/Dates'
import type { Timeslot } from 'types/entities/Timeslot'
import { useTypedSelector } from 'helpers/reducers'
import { useEffect, useRef } from 'react'
import { ActivityType } from 'types/enums/ActivityType'
import type { Activity } from 'types/entities/Activity'
import { TypeTagColor } from 'types/enums/TypeTagColor'
import { CalendarMonthTagType } from 'types/enums/CalendarMonthTagType'
import { ReservationStatus } from 'types/enums/ReservationStatus'
import { Reservation } from 'types/entities/Reservation'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'helpers/store'
import { OffersAction } from 'store/offers/OffersAction'
import { Offer } from 'types/entities/Offer'
// import {countries} from "country-flag-icons";

export const driverPlaceTextSuffix = (i: number) => {
    switch (i) {
        case 1:
            return i18n.t('st')
        case 2:
            return i18n.t('nd')
        case 3:
            return i18n.t('rd')
        default:
            return i18n.t('th')
    }
}

export const getTodayDate = (): number => {
    return Math.floor(Date.now() / 1000)
}

export const lapTime = (laptime: number): string => {
    return (laptime / 100).toFixed(2) + 's'
}

export const percentageTaxFormat = (value: number) => {
    return new Intl.NumberFormat(LOCALE, {
        style: 'percent',
        currency: CURRENCY,
        minimumSignificantDigits: 2,
    }).format(value / 100)
}

export const currencyNumberFormat = (value: number) => {
    return new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency: CURRENCY,
        minimumFractionDigits: CURRENCY_FRACTION_DIGITS,
    }).format(value / 100)
}

export const currencyNumberFormatNoDigit = (value: number) => {
    return new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency: CURRENCY,
        minimumFractionDigits: 0,
    }).format(value / 100)
}

export const floatNumberFormat = (value: number, digits = CURRENCY_FRACTION_DIGITS) => {
    return new Intl.NumberFormat(LOCALE, {
        style: 'decimal',
        minimumFractionDigits: digits,
    }).format(value / 100)
}

export const floatNumberFormatNoDigit = (value: number) => {
    return new Intl.NumberFormat(LOCALE, {
        style: 'decimal',
        minimumFractionDigits: 0,
    }).format(value / 100)
}

export const getArticle = (
    articlesState: ArticlesState,
    articleType: ArticleType,
    personType: AdultOrChild,
    count: number
) => {
    if (PAYMENT_TEST) {
        return articlesState.all?.find((article: Article) => article.article_id === 3000)
    }

    if (articleType === ArticleType.Action) {
        return articlesState.action[personType].find((_, index) => index === 0)
    } else if (articleType === ArticleType.Online) {
        return articlesState.online[personType].find((article: Article) => article.race_count_total === count)
    }
    return undefined
}

export const getArticlePrices = (
    articlesState: ArticlesState,
    articleType: ArticleType,
    personType: AdultOrChild,
    count: number
): PriceOffer => {
    const baseArticle: Article | undefined = getArticle(articlesState, ArticleType.Online, personType, 1)
    const article = getArticle(articlesState, articleType, personType, count)

    if (!article || !baseArticle) {
        return {
            price: 0,
            pricePerRace: 0,
            savings: 0,
        }
    }

    if (articleType === ArticleType.Action) {
        return {
            price: article?.article_value,
            pricePerRace: article?.article_value / article?.race_count_total,
            savings: baseArticle?.article_value * article?.race_count_total - article?.article_value,
        }
    } else {
        return {
            price: article.article_value,
            pricePerRace: article.article_value / article.race_count_total,
            savings: baseArticle.article_value * count - article.article_value,
        }
    }
}

export const getStatsNumberTextByType = (type: string | undefined, number: number) => {
    switch (type) {
        case 'position':
            return number + ' ' + driverPlaceTextSuffix(number)
        case 'time':
            return number + 's'
        case 'number':
            return number
        default:
            return number
    }
}

export const getArticleById = (id: number): Article => {
    return <Article>store.getState().articlesState.all.find((article: Article) => article.id === id)
}

export function getEnumKeyByEnumValue<
    T extends {
        [index: string]: string
    },
>(myEnum: T, enumValue: string): keyof T | null {
    const keys = Object.keys(myEnum).filter((x) => myEnum[x] == enumValue)
    return keys.length > 0 ? keys[0] : null
}

export const serializeFormData = (data: any, formData?: FormData, previousKey?: string) => {
    if (!formData) {
        formData = new FormData()
    }

    Object.keys(data).forEach((key) => {
        const value = data[key]
        let currentKey = key

        // example: object in an array (flags: Flag[])
        if (
            !(value instanceof File) &&
            !(value instanceof FileList) &&
            value instanceof Object &&
            !Array.isArray(value)
        ) {
            serializeFormData(value, formData, currentKey)
            return
        }

        consoleLog('is file...', value)

        if (previousKey) {
            currentKey = `${previousKey}[${key}]`
        }

        if (value instanceof FileList) {
            consoleLog('is file... - FileList', value)
            const files = Array.from(value)
            files.forEach((file, index) => {
                // @ts-ignore We for sure know that formData IS set.
                formData.append(`${currentKey}[${index}]`, file)
            })
        } else if (Array.isArray(value)) {
            consoleLog('is file... - Array', value)
            value.forEach((val, index) => {
                serializeFormData(val, formData, `${currentKey}[${index}]`)
            })
        } else {
            consoleLog('is file... - ELSE', value)
            // @ts-ignore We for sure know that formData IS set.
            formData.append(currentKey, value)
        }
    })
    return formData
}

export const filterPackageItemsByType = (
    Package: Package,
    person_type: PackageItemPersonType | PersonType
): PackageItem[] => {
    return Package.packageItems
        ?.filter((packageItem) => packageItem.person_type === person_type)
        .filter((packageItem) => packageItem.type === PackageItemItemType.PackageItem)
    // .sort((a, b) => {
    //     if(a.position === null && b.position === null) return 0;
    //     if(a.position === null ) return b.position as number;
    //     if(b.position === null ) return a.position as number;
    //     return (
    //         a.position < b.position ? -1 : 1
    //     );
    // });
}

export const personTypeLabel = (person_type: PackageItemPersonType | null) => {
    switch (person_type) {
        case PackageItemPersonType.Adult:
            return i18n.t('Adult person')
        case PackageItemPersonType.Child:
            return i18n.t('Child person')
        case PackageItemPersonType.Event:
            return i18n.t('Event person')
        case PackageItemPersonType.Family:
            return i18n.t('Family person')
        default:
            return i18n.t('Any person')
    }
}

export const translate = (translation: Translations, defaultLanguage: string = '') => {
    if (!translation) {
        return ''
    }
    const language = store.getState().hallState.hall?.language.code
    const userLanguage = store.getState().sessionState.language?.code
    if (!defaultLanguage) {
        defaultLanguage = DEFAULT_LANGUAGE
    }

    if (userLanguage && (translation as any)[userLanguage]) {
        return (translation as any)[userLanguage]
    }
    if (language && (translation as any)[language]) {
        return (translation as any)[language]
    }

    return (translation as any)[defaultLanguage] || (translation as any)['en'] || translation || 'not found'
}

export const truncate = (str: string, length: number = 300, symbol: string = '...') => {
    if (!str) return ''
    if (str.length <= length) return str
    return str.substring(0, length) + symbol
}

const DurationReducer = (accumulator: number, cur: PackageItem) => {
    let accDuration = 0
    const article = cur.article
    if (article && article.race_count_total && typeof article !== 'undefined') {
        accDuration =
            article.race_count_total > 1 ? (cur.duration || 0) * (article.race_count_total || 1) : cur.duration || 0
    }

    if (isNaN(accumulator)) {
        return accDuration
    }
    return accumulator + accDuration
}

export const packageDuration = (packageRow: Package) => {
    const durationByPackageItems =
        uniquePackageWithActivityItem(packageRow, true).reduce(DurationReducer, 0) +
        uniquePackageWithActivityItem(packageRow, false).reduce(DurationReducer, 0)
    const durationByPackage = packageRow.duration
    return durationByPackageItems > durationByPackage ? durationByPackageItems : durationByPackage
}

export const getBreakClaimText = (index: number, type: string) => {
    const gameBreakClaims = ['View and compare the results']

    const activityBreakClaims = [
        'Time to take a quick brake and move on to next activity',
        'You need to walk to another activity and get ready',
        "You're getting ready for your next activity",
        'You need to walk to another activity and get ready',
        'Time to take a quick brake and move on to next activity',
    ]

    return type === 'activity'
        ? i18n.t(activityBreakClaims[index]).toString()
        : i18n.t(gameBreakClaims[index]).toString()
}

export const getPackageItemFromPackageCartItem = (
    packageCartItem: PackagesCartItem,
    package_: Package
): PackageItem | undefined => {
    if (package_.packageItems) {
        return package_.packageItems.find((packageItem) => packageItem.id === packageCartItem.package_item_id)
    }
    return undefined
}

export const padMinutes = (minutes: number) => {
    return ('00' + minutes).slice(-2)
}

export const consoleDebugger = () => {
    debugger // eslint-disable-line no-debugger
}

export const consoleLog = (...params: any[]) => {
    if (IS_PROD) {
        return
    }
    console.log(...params)
}

export const uniqueFilter = <T>(array: T[]): T[] => array.filter((v, i, a) => a.indexOf(v) === i)

export function redirectToReservationInquired(history: any, packagesCart: PackagesCart) {
    // /booking/inquiry/:cart_id/:cart_token
    history.replace('/booking/inquiry/' + packagesCart.id + '/' + packagesCart.token)
}

export const isAdult = (personsType: PersonType | PersonsType | string) => {
    return personsType === PersonType.Adult || personsType === PersonsType.Adult || personsType === 'adult'
}

export const isChild = (personsType: PersonType | PersonsType | string) => {
    return personsType === PersonType.Child || personsType === PersonsType.Child || personsType === 'child'
}

export const isFamily = (personsType: PersonsType | string) => {
    return personsType === PersonsType.Family || personsType === 'family'
}

export const isUnknown = (personsType: PersonsType | string) => {
    return personsType === PersonsType.Unknown || personsType === 'unknown'
}

export const validateByPersonType = (personsType: PersonsType | string): boolean => {
    switch (personsType) {
        case PersonsType.Adult:
            return isAdult(personsType)
        case PersonsType.Child:
            return isChild(personsType)
        case PersonsType.Family:
            return isFamily(personsType)

        default:
            return isUnknown(personsType)
    }
}

export const filterPersonsType = (filter: BookingFilter) => {
    if (filter.children > 0 && filter.adults > 0) {
        return PersonsType.Family
    }
    if (filter.adults > 0) {
        return PersonsType.Adult
    }
    if (filter.children > 0) {
        return PersonsType.Child
    }
    return PersonsType.Unknown
}

export const packagesCartTimeout = (packagesCart: PackagesCart) => {
    const timeNow = new Date().getTime()
    const packagesCartTime = new Date(packagesCart.updated_at).getTime()

    return packagesCartTime + TIMER_COUNT <= timeNow
}

export const getCurrentDriver = (profile: Profile) => {
    return {
        identifier: +profile.id,
        name: profile.name,
        profile_picture_url: profile?.profile_picture_url || null,
        experience: profile.experience,
        person_type: profile.person_type,
        selected: false,
        races_count: profile.ticket_races_left,
        races_assigned_count: profile.ticket_races_left,
    }
}

export const getPersonTypeColorClass = (personType: PersonType) => {
    switch (personType) {
        case PersonType.Adult:
            return 'text-blue-800 bg-blue-400'
        case PersonType.Child:
            return 'text-green-800 bg-green-400'
        default:
            return 'text-indigo-800 bg-indigo-400'
    }
}

export const getReservationTicketColorClass = (personType: PersonType, type: ArticleType) => {
    switch (personType) {
        case PersonType.Adult:
            if (type == ArticleType.Action) {
                return 'text-indigo-800 bg-indigo-400'
            }
            return 'text-blue-800 bg-blue-400'

        case PersonType.Child:
            if (type == ArticleType.Action) {
                return 'text-blue-800 bg-blue-400'
            }
            return 'text-green-800 bg-green-400'
        default:
            return 'text-indigo-800 bg-indigo-400'
    }
}

export const appendQueryParams = (filter: Record<string, string | number | boolean | undefined | null>) => {
    consoleLog(filter)
    return new URLSearchParams(filter as any).toString()
}

export const getPackageItemsWithoutGroupOptions = (Package: Package): PackageItem[] => {
    return Package.packageItems
        .filter((packageItem) => packageItem.type === PackageItemItemType.Upsell)
        .filter((packageItem) => packageItem.extra?.group === null)
}

export const getPackageItemsWithGroupOptions = (Package: Package): PackageItem[] => {
    return Package.packageItems
        .filter((packageItem) => packageItem.type === PackageItemItemType.Upsell)
        .filter((packageItem) => packageItem.extra.group !== null)
}

export const getSelectedUpsell = (upsells: PackageUpsell[], packageItem: PackageItem): PackageUpsell | undefined => {
    return upsells.find((upsell) => upsell.packageItem.id === packageItem.id)
}

export const getNewStep = (bookingState: BookingReducerState, stepId: number): BookingNavigationStep => {
    const steps = bookingState.timeslotsAllowed
        ? bookingState.navigation.timeslotsAllowedSteps
        : bookingState.navigation.timeslotsNotAllowedSteps
    return steps.find((step) => step.id === stepId) || bookingState.navigation.currentStep
}

export const getNewStepGift = (bookingState: BookingReducerState, stepId: number): BookingNavigationStep => {
    const steps = bookingState.timeslotsAllowed
        ? bookingState.navigation.timeslotsGiftAllowedSteps
        : bookingState.navigation.timeslotsNotAllowedSteps
    return steps.find((step) => step.id === stepId) || bookingState.navigation.currentStep
}
export const sortPackageItemsByPosition = (packageItems: PackageItem[]) => {
    // IMPORTANT: do not mutate original array (can come directly from Redux state)
    // Always work on a shallow copy to avoid invariant middleware errors.
    return [...packageItems].sort((a, b) => {
        if (a.position === null || b.position === null) return 1
        return a.position > b.position ? 1 : -1
    })
}

export const packageItemUpsellPersonType = (
    bookingFilter: BookingFilter,
    personType: PackageItemPersonType | null
): boolean => {
    if (!personType) return true
    if (personType === PackageItemPersonType.Any) return true
    if (bookingFilter.adults > 0 && personType === PackageItemPersonType.Adult) return true
    if (bookingFilter.children > 0 && personType === PackageItemPersonType.Child) return true
    return false
}

export const copyToClipboard = async (text?: string): Promise<boolean> => {
    try {
        const toCopy = text || location.href
        await navigator.clipboard.writeText(toCopy)
        toast.success(i18n.t('Copied to clipboard') + ': ' + toCopy)
        return true
    } catch (err) {
        console.error('Failed to copy: ', err)
        return false
    }
}

export const subtractDaysFromDates = (dateFrom: Date, days: number): Date => {
    const result = new Date(dateFrom)
    result.setDate(result.getDate() - days)
    return result
}

export const range = (size: number, startAt = 0) => {
    // @ts-ignore
    return [...Array(size).keys()].map((i) => i + startAt)
}

export const phoneValidation = (phone: string | number) => {
    if (phone === null || phone === undefined || phone.toString().length === 0 || phone === 0 || phone === '') {
        return false
    }
    const phoneTrimmed = phone.toString().replace(/\s/g, '')
    const regex =
        /(\+\d{1,3}\s?)?((\(\d{3}\)\s?)|(\d{3})(\s|-?))(\d{3}(\s|-?))(\d{3,6})(\s?(([E|e]xt[:|.|]?)|x|X)(\s?\d+))?/g
    return regex.test(phoneTrimmed)
}

export const emailValidation = (email: string) => {
    const emailTrimmed = email.replace(/\s/g, '')
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i
    return regex.test(emailTrimmed)
}

export const checkAddress = (addressData: { city: string; street_address: string; postcode: string }) => {
    const url = 'https://service.post.ch/zopa/app/api/addresschecker/v1/houses/search?noCache='
    const timestamp = new Date().getTime()

    const numb = addressData?.street_address?.match(/\d/g)
    const houseNumber = numb ? numb.join('') : null
    const { city, street_address } = addressData
    const params = {
        city: city,
        street: street_address,
        limit: 60,
        houseNbr: houseNumber,
        noCache: timestamp,
    }
    const result = ajax.get(url, { params: params })
    console.log(result)
}

export const listOfAllRequiredFieldsForAccount = (_account: Driver): string[] => {
    const baseFields = ['name', 'last_name', 'nickname', 'birthdate']
    const addressFields = ['address', 'country', 'zip', 'town']
    const contactFields = ['email', 'phone']
    const userTypeToRequiredFields: Record<UserType, string[]> = {
        [UserType.User]: [
            ...baseFields,
            ...(REQUIRE_FULL_ADDRESS_IN_REGISTRATION ? addressFields : []),
            ...contactFields,
        ],
        [UserType.Guest]: [
            ...baseFields,
            ...(GUEST_ADDRESS_MANDATORY ? addressFields : []),
            ...(GUEST_PHONE_EMAIL_MANDATORY ? contactFields : []),
        ],
        [UserType.Seller]: [...baseFields, ...contactFields],
    }
    return userTypeToRequiredFields[UserType.Guest] || baseFields
}

export const listOfAllMissingRequiredData = (account: Driver): string[] => {
    return (listOfAllRequiredFieldsForAccount(account) as (keyof Driver)[]).filter(
        (name) => !account[name] || account[name] === ''
    )
}

export const isValidPhoneNumberFormat = (phoneNumber: string) => {
    if (phoneNumber.startsWith('+')) {
        const parsePhone = parsePhoneNumberFromString(phoneNumber)
        return parsePhone?.isValid()
    }
    return false
}

export const checkIfAllAccountHasCorrectData = (accounts: ReservationDriver[]): boolean => {
    const allAccountsHaveAllRequiredData = accounts.every(
        (account) => account?.driver && listOfAllMissingRequiredData(account.driver).length === 0
    )
    const allAccountsHaveCorrectPhoneData = accounts.every(
        (account) => account?.driver?.phone && isValidPhoneNumberFormat(account.driver.phone)
    )
    return allAccountsHaveAllRequiredData && allAccountsHaveCorrectPhoneData
}


export const REGEX_EMAIL = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i

export const sanitizedInputs = <T extends Record<string, any>>(payload: T): T => {
    Object.keys(payload).forEach((k) => {
        const v = (payload as any)[k]
        if (v === undefined || (typeof v === 'string' && v.trim() === '')) {
            delete (payload as any)[k]
        }
    })

    return payload
}

export const enumToOptions = (e: Record<string, any>) =>
    Object.keys(e).map((k) => ({ label: k, description: '', value: e[k] }))

export function resolveTitleFromPath(pathname: string): string {
    if (!pathname || pathname === '/') return 'Dashboard'

    const segments = pathname.split('/').filter(Boolean)
    const first = segments[0] || 'Dashboard'
    if (first === 'offer-email-templates') return 'Email Templates'

    // Replace underscores with spaces and capitalize each word
    return first
        .split(/[-_]/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
}

export const createCalendarLink = (
    view: string,
    year: number | string,
    month: number | string,
    day: number | string
): string => {
    const date = Dates.dayjs()
    if (!isNumeric(String(year))) {
        year = date.year()
    }
    if (!isNumeric(String(month))) {
        month = date.month() + 1
    }
    if (!isNumeric(String(day))) {
        day = date.date()
    }

    return `/calendar/${view}/${year}/${month}/${day}`
}

export const isNumeric = (str: any) => {
    if (typeof str != 'string') return false // we only process strings!
    return (
        !isNaN(str as any) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
        !isNaN(parseFloat(str))
    ) // ...and ensure strings of whitespace fail
}

export const getQuarterHourIntervals = (startHour: number, endHour: number) => {
    const quarterHours = ['00', '15', '30', '45']
    const times = []
    for (let i = startHour; i < endHour; i++) {
        for (let j = 0; j < 4; j++) {
            const time = i + ':' + quarterHours[j]
            if (i < 10) {
                times.push('0' + time)
            } else {
                times.push(time)
            }
        }
    }
    return times
}

export const getQuarterHourIntervalsFromTo = (
    startHour: number,
    startMinute: number,
    endHour: number,
    endMinute: number
) => {
    const quarterHours = ['00', '15', '30', '45']
    const times = []
    for (let i = startHour; i <= endHour; i++) {
        for (let j = 0; j < 4; j++) {
            if (i === startHour && parseInt(quarterHours[j]) < startMinute) {
                continue
            }
            if (i === endHour && parseInt(quarterHours[j]) > endMinute) {
                continue
            }

            const hour = 0
            if (i < 10) {
                times.push('0' + (i - hour) + ':' + quarterHours[j])
            } else {
                times.push(i - hour + ':' + quarterHours[j])
            }
        }
    }
    return times
}

export const toRoman = (num: number): string => {
    if (isNaN(num)) return ''
    const digits = String(+num).split('')
    const key = [
        '',
        'C',
        'CC',
        'CCC',
        'CD',
        'D',
        'DC',
        'DCC',
        'DCCC',
        'CM',
        '',
        'X',
        'XX',
        'XXX',
        'XL',
        'L',
        'LX',
        'LXX',
        'LXXX',
        'XC',
        '',
        'I',
        'II',
        'III',
        'IV',
        'V',
        'VI',
        'VII',
        'VIII',
        'IX',
    ]
    let roman = ''
    let i = 3
    while (i--) roman = (key[+digits.pop()! + i * 10] || '') + roman
    return Array(+digits.join('') + 1).join('M') + roman
}

export const filterTimeslotsByHoursRange = (timeslots: Timeslot[], start_at: number, end_at: number): Timeslot[] => {
    return timeslots.filter((timeslot) => {
        //if 0 is smaller than 23
        if (Dates.hour(timeslot.start_at) >= end_at) {
            return false
        } else {
            return Dates.hour(timeslot.start_at) >= start_at && Dates.hour(timeslot.end_at) <= end_at
        }
    })
}

export const getHallOpeningHours = (calendarDay: Date = new Date()): [number, number] => {
    const openingHours = useTypedSelector((state) => state.calendar.day)?.openingHours || []

    const openingHoursForToday = openingHours.find((openingHour) => {
        return new Date(openingHour.date).toLocaleDateString() === calendarDay.toLocaleDateString()
    })
    let start_at = 0
    let end_at = 24

    if (openingHoursForToday) {
        if (openingHoursForToday.to_hours < openingHoursForToday.from_hours) {
            end_at = openingHoursForToday.to_hours + 24
        } else {
            end_at = openingHoursForToday.to_hours
        }
        start_at = openingHoursForToday.from_hours
    }

    return [start_at, end_at]
}

export const filterTimeslotsByOpeningHours = (timeslots: Timeslot[]): Timeslot[] => {
    const [start_at, end_at] = getHallOpeningHours()
    return filterTimeslotsByHoursRange(timeslots, start_at, end_at)
}

export const useTimePosition = (date: Date): { position: number } => {
    const start_at = useTypedSelector((state) => state.settings.config?.timeslots_open_at) || 0
    const end_at = useTypedSelector((state) => state.settings.config?.timeslots_close_at) || 0
    let position = 0
    position = (((Dates.hour(date) * 60 + Dates.minute(date)) / 60 - start_at) / (end_at - start_at)) * 100
    // end of day - start of day = range / (current time - start of day)
    return {
        position: position,
    }
}

//https://overreacted.io/making-setinterval-declarative-with-react-hooks/
export function useInterval(callback: () => void, delay?: number | null): void {
    const savedCallback = useRef<(() => void) | null>(null)

    // Remember the latest function.
    useEffect(() => {
        savedCallback.current = callback
    }, [callback])

    // Set up the interval.
    useEffect(() => {
        function tick() {
            if (savedCallback.current) {
                // @ts-ignore
                savedCallback?.current()
            }
        }

        if (delay !== null && delay !== undefined) {
            const id = setInterval(tick, delay)
            return (): void => {
                clearInterval(id)
            }
        }
        return undefined
    }, [delay])
}

export const getActivityTagBgColor = (activity: Activity): string => {
    switch (activity.type) {
        case ActivityType.Karting:
            return 'bg-red-400'
        case ActivityType.TheParkPlayground:
            return 'bg-blue-600'
        case ActivityType.Points:
            return 'bg-green-600'
        case ActivityType.Time:
            return 'bg-purple-600'
        default:
            return 'bg-yellow-500'
    }
}

export const parseInteger = (param: string): number => {
    return parseInt(param, 10)
}

export const getLeftTimeslotPosition = (timeslot: Timeslot, minuteSize: number): number => {
    const today = useTypedSelector((state) => state.calendar.day)?.date || new Date()
    const [start_at, end_at] = getHallOpeningHours(new Date(today))
    if (end_at < start_at) {
        return (
            (Dates.hour(timeslot.start_at + 24) - start_at) * minuteSize * 60 +
            Dates.minute(timeslot.start_at) * minuteSize
        )
    } else {
        return (
            (Dates.hour(timeslot.start_at) - start_at) * minuteSize * 60 + Dates.minute(timeslot.start_at) * minuteSize
        )
    }
}

export const getLeftOfferPosition = (hour: number, minute: number, minuteSize: number): number => {
    const today = useTypedSelector((state) => state.calendar.day)?.date || new Date()
    const [start_at, end_at] = getHallOpeningHours(new Date(today))
    if (end_at < start_at) {
        return ((hour < start_at ? hour + 24 : hour) - start_at) * minuteSize * 60 + minute * minuteSize
    } else {
        return (hour - start_at) * minuteSize * 60 + minute * minuteSize
    }
}

export const getLeftTimeslotGridPosition = (
    timeslot: Timeslot,
    minuteSize: number,
    start_at: number,
    end_at: number
): number => {
    if (end_at < start_at) {
        return (
            (Dates.hour(timeslot.start_at + 24) - start_at) * minuteSize * 60 +
            Dates.minute(timeslot.start_at) * minuteSize
        )
    } else {
        return (
            (Dates.hour(timeslot.start_at) - start_at) * minuteSize * 60 + Dates.minute(timeslot.start_at) * minuteSize
        )
    }
}

export const getGroupNameTag = (person_type: string | PersonsType): { color: TypeTagColor; label: PersonsType } => {
    switch (person_type) {
        case PersonsType.Adult:
            return {
                label: PersonsType.Adult,
                color: TypeTagColor.Blue,
            }
        case PersonsType.Child:
            return {
                label: PersonsType.Child,
                color: TypeTagColor.Green,
            }
        case PersonsType.Family:
            return {
                label: PersonsType.Family,
                color: TypeTagColor.Orange,
            }
        default:
            return {
                label: PersonsType.Unknown,
                color: TypeTagColor.Gray,
            }
    }
}
export const getCalendarTagColor = (tagType: CalendarMonthTagType): string => {
    switch (tagType) {
        case CalendarMonthTagType.Events:
            return 'bg-red-500'
        case CalendarMonthTagType.Standard:
            return 'bg-blue-500'
        case CalendarMonthTagType.Offers:
            return 'bg-orange-500'
        case CalendarMonthTagType.Leads:
            return 'bg-purple-500'
        case CalendarMonthTagType.Reservations:
            return 'bg-green-500'
        default:
            return 'bg-gray-500'
    }
}

export const getCalendarOpeningHoursTimes = (hour: number = 24): string[] => {
    const day = useTypedSelector((state) => state.calendar.day)
    const today = day?.date || new Date()
    const [start_at, end_at] = getHallOpeningHours(new Date(today))
    const quarterHours = ['00', '15', '30', '45']
    const times: string[] = []
    for (let i = start_at; i < end_at + 1; i++) {
        for (let j = 0; j < 4; j++) {
            if (i < hour) {
                times.push(i + ':' + quarterHours[j])
            } else {
                times.push(i - hour + ':' + quarterHours[j])
            }
        }
    }
    return times
}

export const getReservationStatusClass = (reservation: Reservation): string => {
    switch (reservation.status) {
        case ReservationStatus.Creating:
            return 'bg-amber-500'
        case ReservationStatus.Pending:
            return 'bg-green-400'
        case ReservationStatus.Rebooking:
            return 'bg-green-300'
        case ReservationStatus.Confirmed:
            return 'bg-green-500'
        case ReservationStatus.Reserved:
            return 'bg-green-600'
        case ReservationStatus.Finished:
            return 'bg-slate-500'
        case ReservationStatus.Cancelled:
            return 'bg-red-500'
        case ReservationStatus.Deleted:
            return 'bg-red-500'
        case ReservationStatus.NoShow:
            return 'bg-red-500'
        default:
            return 'bg-gray-700'
    }
}

export const getPersonsTypeClass = (personsType: PersonsType | string): string => {
    switch (personsType) {
        case PersonsType.Adult:
            return 'bg-blue-400'
        case PersonsType.Child:
            return 'bg-green-400'
        case PersonsType.Family:
            return 'bg-yellow-400'
        case PersonsType.Unknown:
            return 'bg-gray-700'
        default:
            return 'bg-gray-700'
    }
}

export const getSaveAndCloseLabel = (
    showSaveAndClose: boolean,
    isSubmitting: boolean,
    savingLabel: string,
    saveAndCloseLabel: string
) => {
    if (!showSaveAndClose) {
        return undefined
    }

    return isSubmitting ? savingLabel : saveAndCloseLabel
}

type NoteField = 'offer_note' | 'invoice_note'

type OfferExtraNoteInputs = {
    offer_note?: string | null
    invoice_note?: string | null
}

const OfferExtraNoteSchema = z.object({
    offer_note: z.string().nullable().optional(),
    invoice_note: z.string().nullable().optional(),
})

interface UseOfferExtraNoteFormProps {
    offer: Offer
    noteField: NoteField
    successMessage: string
    errorMessage: string
}

export const useOfferExtraNoteForm = ({
    offer,
    noteField,
    successMessage,
    errorMessage,
}: UseOfferExtraNoteFormProps) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const defaultNote = offer.extra?.[noteField] ?? null

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
        getValues,
        getFieldState,
    } = useForm<OfferExtraNoteInputs>({
        resolver: zodResolver(OfferExtraNoteSchema),
        defaultValues: {
            [noteField]: defaultNote,
        },
    })

    const currentNote = watch(noteField)
    const pendingNoteSaves = useRef(0)
    const lastSubmittedNote = useRef<string | null>(null)
    useEffect(() => {
        // Refresh a clean field after another editor saves; retain a local draft.
        if (!getFieldState(noteField).isDirty || getValues(noteField) === defaultNote) {
            reset({[noteField]: defaultNote})
        }
    }, [defaultNote, noteField, getFieldState, getValues, reset])
    const isNoteChanged = currentNote !== defaultNote

    const onErrorHandler = (error: unknown) => {
        consoleLog('onErrorHandler', error)
        toast.error(t('Please fix validation errors'))
    }

    const onSubmitHandler = (data: OfferExtraNoteInputs) => {
        const nextNote = data[noteField] ?? ''
        if (pendingNoteSaves.current > 0 ? lastSubmittedNote.current === nextNote : nextNote === (defaultNote ?? '')) return
        pendingNoteSaves.current++
        lastSubmittedNote.current = nextNote
        setIsLoading(true)

        return dispatch(OffersAction.updateOfferExtraNote(offer.id, noteField, nextNote))
            .then(() => {
                toast.success(t(successMessage))
            })
            .catch((error) => {
                consoleLog('error', error)
                toast.error(t(errorMessage))
            })
            .finally(() => {
                pendingNoteSaves.current--
                setIsLoading(pendingNoteSaves.current > 0)
            })
    }

    return {
        register,
        handleSubmit,
        errors,
        isLoading,
        isNoteChanged,
        onErrorHandler,
        onSubmitHandler,
    }
}

type SearchFilterItem = {
    id: string
    value: any
}

export const toSearchFilterList = (
    filters: SearchFilterItem[] | SearchFilterItem | undefined
): SearchFilterItem[] => {
    return Array.isArray(filters) ? filters : filters ? [filters] : []
}

export const expandSearchFiltersWithMap = (
    filters: SearchFilterItem[] | SearchFilterItem | undefined,
    searchMap: Record<string, string[]>
): SearchFilterItem[] => {
    const list = toSearchFilterList(filters)

    return list.flatMap((filter) => {
        if (!filter?.id) {
            return []
        }

        const fields = searchMap[filter.id]
        return fields ? fields.map((field) => ({ id: field, value: filter.value })) : [filter]
    })
}

type PackagesCartDrivers = {
    drivers_count_child?: number | null
    drivers_count_adult?: number | null
}

export const getPackagesCartPersonsCount = (packagesCart?: PackagesCartDrivers | null) => {
    if (!packagesCart) {
        return 0
    }

    return (packagesCart.drivers_count_child ?? 0) + (packagesCart.drivers_count_adult ?? 0)
}

export const getOfferPersonsCount = (offer?: { packagesCart?: PackagesCartDrivers | null } | null) => {
    if (!offer) {
        return 0
    }

    return getPackagesCartPersonsCount(offer.packagesCart)
}

export const isInNumberRangeFilter = (value: number, filterValue: unknown) => {
    if (!filterValue || typeof filterValue !== 'object') {
        return true
    }

    const filter = filterValue as { from?: number; to?: number }

    if (filter.from !== undefined && value < filter.from) {
        return false
    }

    if (filter.to !== undefined && value > filter.to) {
        return false
    }

    return true
}

export const numberRangeColumnFilter = (row: any, columnId: string, filterValue: unknown) => {
    const value = row.getValue(columnId) as number
    return isInNumberRangeFilter(value, filterValue)
}
